import { useState } from "react";
import { CheckCircle, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const SEOAuditWidget = () => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2, triggerOnce: true });
  const navigate = useNavigate();

  const benefits = [
    "See where local visibility is leaking",
    "Find trust and conversion gaps fast",
    "Get a practical next-step recommendation",
    "100% free, no obligations"
  ];

  const handleAnalyze = () => {
    if (websiteUrl.trim()) {
      navigate(`/funnel/local-seo?website=${encodeURIComponent(websiteUrl.trim())}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAnalyze();
    }
  };

  return (
    <section ref={ref} id="seo-audit" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Value proposition */}
            <div className={cn(
              "transition-all duration-700",
              isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}>
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-accent/20 to-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-sm">
                <Search className="h-4 w-4" />
                Free Authority Scan
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                See Why Your Website and Local Presence Are Winning or Losing
              </h2>

              <p className="text-lg text-muted-foreground mb-6">
                Get a Local SEO + Website Authority Scan. Discover where visibility breaks down, where trust is weak, and what to fix first.
              </p>

              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li 
                    key={index} 
                    className={cn(
                      "flex items-center gap-3 group transition-all duration-500",
                      isInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{ transitionDelay: `${200 + index * 100}ms` }}
                  >
                    <div className="p-1 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                      <CheckCircle className="h-4 w-4 text-accent" />
                    </div>
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: SEO Audit Widget */}
            <Card 
              variant="premium" 
              shimmer 
              className={cn(
                "group transition-all duration-700",
                isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: "300ms" }}
            >
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-accent/20 group-hover:shadow-xl group-hover:shadow-accent/30 transition-all duration-300 group-hover:scale-105">
                    <Search className="h-8 w-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Free Authority Scan
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Enter your website URL and we will tee up your scan request
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    type="url"
                    placeholder="Enter your website URL (e.g., yoursite.com)"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="text-base h-12"
                    aria-label="Enter your website URL"
                  />
                  <Button 
                    onClick={handleAnalyze}
                    className="w-full h-12 text-base group/btn shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
                    disabled={!websiteUrl.trim()}
                  >
                    <Search className="mr-2 h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                    Start My Free Scan
                    <Sparkles className="ml-2 h-4 w-4 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Takes less than 60 seconds • No credit card required
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOAuditWidget;
